import dayjs, { Dayjs } from 'dayjs';

// --- Types ---

export type RecurrenceType = 'daily' | 'weekly' | 'monthly' | 'custom';

export type MonthlyMode = 'dayOfMonth' | 'nthWeekday';

export interface RecurrencePattern {
    type: RecurrenceType;
    interval: number; // every N days/weeks/months (ignored for custom)
    weekdays: number[]; // 0=Sun, 1=Mon, ..., 6=Sat (for weekly)
    monthlyMode: MonthlyMode; // for monthly: by date or by nth weekday
    dayOfMonth: number; // e.g. 15 (for monthly by date)
    nthWeekday: { n: number, weekday: number }; // e.g. { n: 2, weekday: 2 } = 2nd Tuesday (for monthly by weekday)
    endCondition: 'untilDate' | 'afterOccurrences';
    endDate: Dayjs | null; // for untilDate
    occurrences: number; // for afterOccurrences
    customDates: Dayjs[]; // for custom type
}

export interface GeneratedDate {
    date: Dayjs;
    startTime: string; // HH:mm
    endTime: string; // HH:mm
    included: boolean; // can be deselected by user
    hasConflict?: boolean; // true if the booking guard would refuse the date
    /**
     * The date is taken by another booking and may be created anyway, as a
     * conflict (admin series only, booking-conflict plan decision 0). False
     * for a refusal that is not about occupancy (no bookable object, an
     * unresolvable pool): such a date can never be created.
     */
    canCreateAsConflict?: boolean;
    /**
     * The holiday the date falls on (M9, #3041), when the tenant has a holiday
     * region. A public holiday arrives deselected and is booked only when the
     * admin ticks it (`includedHolidaysOf`); a school holiday is only marked.
     */
    holiday?: PreviewHoliday;
}

// --- Default Pattern ---

export const defaultRecurrencePattern: RecurrencePattern = {
    type: 'weekly',
    interval: 1,
    weekdays: [],
    monthlyMode: 'dayOfMonth',
    dayOfMonth: 1,
    nthWeekday: { n: 1, weekday: 1 },
    endCondition: 'afterOccurrences',
    endDate: null,
    occurrences: 10,
    customDates: [],
};

// --- Server pattern ---
//
// Dates are expanded on the SERVER (series plan
// docs/plans/series-booking-module.md §4.3: `POST /admin/series/preview`), in
// the tenant's time zone and under the 104-occurrence cap. The browser used to
// generate them here, in the admin's own time zone and without a cap; this
// file now only holds the form's vocabulary and its translation to the API.

/** The pattern as the series routes take it (new-backend `SeriesPattern`). */
export interface ServerSeriesPattern {
    type: RecurrenceType;
    interval?: number;
    weekdays?: number[];
    dayOfMonth?: number;
    nthWeekday?: { n: number, weekday: number };
    startDate: string;
    endCondition: { untilDate: string } | { occurrences: number };
    customDates?: string[];
}

const ISO_DATE = 'YYYY-MM-DD';

/** The server's cap on one series (new-backend `DEFAULT_MAX_OCCURRENCES`, pinned by recurrence.test.ts). */
export const MAX_SERIES_OCCURRENCES = 104;

/** The form's end condition as the series routes take it. */
export const serverEndCondition = (
    pattern: Pick<RecurrencePattern, 'endCondition' | 'endDate' | 'occurrences'>,
): ServerSeriesPattern['endCondition'] => (
    pattern.endCondition === 'untilDate' && pattern.endDate
        ? { untilDate: pattern.endDate.format(ISO_DATE) }
        : { occurrences: pattern.occurrences }
);

/** The form state, anchored on `startDate`, as the series routes take it. */
export function toServerPattern(pattern: RecurrencePattern, startDate: Dayjs): ServerSeriesPattern {
    const endCondition = serverEndCondition(pattern);
    const base = { type: pattern.type, startDate: startDate.format(ISO_DATE), endCondition };
    switch (pattern.type) {
        case 'weekly':
            return { ...base, interval: pattern.interval, weekdays: pattern.weekdays };
        case 'monthly':
            return pattern.monthlyMode === 'nthWeekday'
                ? { ...base, interval: pattern.interval, nthWeekday: pattern.nthWeekday }
                : { ...base, interval: pattern.interval, dayOfMonth: pattern.dayOfMonth };
        case 'custom':
            return { ...base, customDates: pattern.customDates.map((d) => d.format(ISO_DATE)) };
        default:
            return { ...base, interval: pattern.interval };
    }
}

/** One date of the server preview. */
/** A holiday on a preview date (`POST /admin/series/preview`, M9). */
export interface PreviewHoliday {
    kind: 'public' | 'school';
    /** The source's names per language, e.g. `{ language: 'DE', text: 'Neujahr' }`. */
    name: { language: string, text: string }[];
    /** A public holiday: the server leaves it out unless it is named in `includedHolidays`. */
    defaultExcluded: boolean;
    /** An allocation window on the room pauses on this date (D7): the hall time is free. */
    windowPaused?: boolean;
}

export interface PreviewOccurrence {
    date: string;
    start: string;
    end: string;
    status: 'available' | 'conflict';
    /** On a conflict: `occupied` (taken, can be created anyway) or `unavailable`. */
    reason?: 'occupied' | 'unavailable';
    /** Absent when the date is no holiday or the tenant has no holiday region. */
    holiday?: PreviewHoliday;
}

/**
 * The preview as the review list shows it: every date. A date taken by another
 * booking stays selected and is created as a conflict unless the admin removes
 * it (booking-conflict plan decision 0); a date refused for any other reason
 * arrives deselected, because it can never be created.
 */
export function datesFromPreview(
    occurrences: PreviewOccurrence[],
    startTime: string,
    endTime: string,
): GeneratedDate[] {
    return occurrences.map((o) => ({
        date: dayjs(o.date),
        startTime,
        endTime,
        included: (o.status === 'available' || (o.status === 'conflict' && o.reason === 'occupied'))
            && !o.holiday?.defaultExcluded,
        hasConflict: o.status === 'conflict',
        canCreateAsConflict: o.status === 'conflict' && o.reason === 'occupied',
        ...(o.holiday ? { holiday: o.holiday } : {}),
    }));
}

/**
 * The dates the create request keeps despite a conflict: the selected ones the
 * preview found taken. The server creates them as conflicts; every other date
 * stays under the booking guard.
 */
/** The public holidays the admin ticked: booked although the server leaves them out by default. */
export function includedHolidaysOf(dates: GeneratedDate[]): string[] {
    return dates
        .filter((d) => d.included && d.holiday?.defaultExcluded)
        .map((d) => d.date.format('YYYY-MM-DD'));
}

/** The holiday's name in `language` (`de` / `en`), else the first one. */
export function holidayNameOf(holiday: Pick<PreviewHoliday, 'name'>, language: string): string {
    const wanted = language.slice(0, 2).toUpperCase();
    return (holiday.name.find((n) => n.language === wanted) ?? holiday.name[0])?.text ?? '';
}

export function forcedDatesOf(dates: GeneratedDate[]): string[] {
    return dates
        .filter((d) => d.included && d.hasConflict && d.canCreateAsConflict)
        .map((d) => d.date.format('YYYY-MM-DD'));
}

// --- Weekday Labels ---

export const WEEKDAY_LABELS_DE: Record<number, string> = {
    0: 'So',
    1: 'Mo',
    2: 'Di',
    3: 'Mi',
    4: 'Do',
    5: 'Fr',
    6: 'Sa',
};

export const WEEKDAY_LABELS_EN: Record<number, string> = {
    0: 'Sun',
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat',
};

export const NTH_LABELS_DE: Record<number, string> = {
    1: 'Ersten',
    2: 'Zweiten',
    3: 'Dritten',
    4: 'Vierten',
    [-1]: 'Letzten',
};

export const NTH_LABELS_EN: Record<number, string> = {
    1: 'First',
    2: 'Second',
    3: 'Third',
    4: 'Fourth',
    [-1]: 'Last',
};

// --- Pattern Summary ---

/** "1 ausgewählter Termin" / "3 ausgewählte Termine" — singular and plural differ. */
const selectedDatesSummary = (count: number, lang: 'de' | 'en'): string => {
    if (lang === 'de') return count === 1 ? '1 ausgewählter Termin' : `${count} ausgewählte Termine`;
    return count === 1 ? '1 selected date' : `${count} selected dates`;
};

const dailySummary = (interval: number, lang: 'de' | 'en'): string => {
    if (interval === 1) return lang === 'de' ? 'Täglich' : 'Daily';
    return lang === 'de' ? `Alle ${interval} Tage` : `Every ${interval} days`;
};

const weeklySummary = (interval: number, days: string, lang: 'de' | 'en'): string => {
    if (interval === 1) return lang === 'de' ? `Wöchentlich: ${days}` : `Weekly: ${days}`;
    return lang === 'de' ? `Alle ${interval} Wochen: ${days}` : `Every ${interval} weeks: ${days}`;
};

const monthlySummary = (pattern: RecurrencePattern, lang: 'de' | 'en'): string => {
    if (pattern.monthlyMode === 'dayOfMonth') {
        return lang === 'de' ? `Monatlich am ${pattern.dayOfMonth}.` : `Monthly on the ${pattern.dayOfMonth}th`;
    }
    const weekdayLabels = lang === 'de' ? WEEKDAY_LABELS_DE : WEEKDAY_LABELS_EN;
    const nthLabels = lang === 'de' ? NTH_LABELS_DE : NTH_LABELS_EN;
    const nth = `${nthLabels[pattern.nthWeekday.n]} ${weekdayLabels[pattern.nthWeekday.weekday]}`;
    return lang === 'de' ? `Monatlich am ${nth}` : `Monthly on the ${nth}`;
};

export function getPatternSummary(pattern: RecurrencePattern, lang: 'de' | 'en' = 'de'): string {
    switch (pattern.type) {
        case 'daily':
            return dailySummary(pattern.interval, lang);
        case 'weekly': {
            const weekdayLabels = lang === 'de' ? WEEKDAY_LABELS_DE : WEEKDAY_LABELS_EN;
            return weeklySummary(pattern.interval, pattern.weekdays.map((d) => weekdayLabels[d]).join(', '), lang);
        }
        case 'monthly':
            return monthlySummary(pattern, lang);
        case 'custom':
            return selectedDatesSummary(pattern.customDates.length, lang);
        default:
            return '';
    }
}
